// Angular Files
import { Injectable } from '@angular/core';

// Other External Files
import { firstValueFrom } from 'rxjs';

// Teller Online Files
import {
    CreateScheduledPaymentRequestDto,
    CreateScheduledPaymentResponseDto,
    GetInstanceBreakdownResponseDto,
    GetSchedulingDetailsResponseDto,
    InstanceConvenienceFeeDto,
    ScheduledPaymentDto,
    ScheduledPaymentIntervalEnumDto,
    ScheduledPaymentsApiClient,
    UserScheduledPaymentSummaryDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import {
    PaymentMethodTypeEnumDto
} from 'libraries/core/src/lib/api/CoreWebApi';

export type ConvenienceFeeMap = {
    [key in PaymentMethodTypeEnumDto]?: number;
};

export interface ScheduledPayment {
    date: string;
    amount: number;
    convenienceFees: ConvenienceFeeMap;
}

export interface ScheduledPaymentPreview {
    payments: ScheduledPayment[];
    totalConvenienceFees: ConvenienceFeeMap;
}

@Injectable({
    providedIn: 'root'
})
export class ScheduledPaymentsService {
    constructor(
        private scheduledPaymentsApiClient: ScheduledPaymentsApiClient
    ) {
    }

    /** Create a new scheduled payment */
    public async createScheduledPayment(
        request: CreateScheduledPaymentRequestDto
    ): Promise<CreateScheduledPaymentResponseDto> {
        return firstValueFrom(
            this.scheduledPaymentsApiClient
                .createScheduledPayment(request)
        );
    }

    /** Get scheduling details for a specific BSI */
    public async getSchedulingDetails(
        pageId: number,
        businessSystemItemKey: string
    ): Promise<GetSchedulingDetailsResponseDto> {
        return firstValueFrom(
            this.scheduledPaymentsApiClient
                .getSchedulingDetails(
                    pageId, businessSystemItemKey
                )
        );
    }

    /** Preview scheduled payment instances */
    public async getInstanceBreakdown(
        pageId: number,
        amount: number,
        startDate: string,
        endDate: string | undefined,
        interval: ScheduledPaymentIntervalEnumDto
    ): Promise<GetInstanceBreakdownResponseDto> {
        return firstValueFrom(
            this.scheduledPaymentsApiClient
                .getInstanceBreakdown(
                    pageId, amount, startDate,
                    endDate, interval
                )
        );
    }

    /**
     * Fetches a payment schedule preview with mapped
     * convenience fees and totals. Delegates all
     * computation (date enumeration, amount splitting)
     * to the backend.
     */
    public async getScheduledPaymentPreview(
        pageId: number,
        amount: number,
        startDate: string,
        endDate: string | undefined,
        frequency: ScheduledPaymentIntervalEnumDto
    ): Promise<ScheduledPaymentPreview> {
        const breakdown = await this.getInstanceBreakdown(
            pageId, amount, startDate, endDate, frequency
        );

        const payments: ScheduledPayment[] = breakdown.instances.map((instance) => ({
                date: instance.instanceDate,
                amount: instance.instanceAmount,
                convenienceFees: this.mapConvenienceFees(
                    instance.convenienceFees
                )
            }));

        const totalConvenienceFees = this.calculateTotalConvenienceFees(payments);

        return { payments, totalConvenienceFees };
    }

    /** Cancel a scheduled payment */
    public async cancelScheduledPayment(
        guid: string
    ): Promise<void> {
        return firstValueFrom(
            this.scheduledPaymentsApiClient
                .cancelScheduledPayment(guid)
        );
    }

    /** Retrieve a scheduled payment with all its instances */
    public async getScheduledPayment(
        guid: string
    ): Promise<ScheduledPaymentDto> {
        return firstValueFrom(
            this.scheduledPaymentsApiClient
                .getScheduledPayment(guid)
        );
    }

    /** Retrieve all scheduled payments for the current user */
    public async getUserScheduledPayments(
    ): Promise<UserScheduledPaymentSummaryDto[]> {
        return firstValueFrom(
            this.scheduledPaymentsApiClient
                .getUserScheduledPayments()
        );
    }

    private mapConvenienceFees(
        fees: InstanceConvenienceFeeDto[]
    ): ConvenienceFeeMap {
        const result: ConvenienceFeeMap = {};

        fees?.forEach((f) => {
            result[
                f.paymentMethodType as PaymentMethodTypeEnumDto
            ] = f.fee;
        });

        return result;
    }

    private calculateTotalConvenienceFees(
        payments: ScheduledPayment[]
    ): ConvenienceFeeMap {
        const totals: ConvenienceFeeMap = {};

        payments.forEach((payment) => {
            Object.entries(payment.convenienceFees)
                .forEach(([type, fee]) => {
                    const key = type as PaymentMethodTypeEnumDto;

                    totals[key] = (totals[key] ?? 0) + (fee ?? 0);
                });
        });

        return totals;
    }
}
